// SF5 — the group before the list.
//
// Individually these terms are noise and the whole claim is what they are
// worth together, so the segment is stated first, then the per-product split,
// then the terms the seller is not on at all.
import React from 'react';
import s from '../SearchAttribution.module.css';
import { money, num, pct } from '../format';

export default function LongTail({ data, loading }) {
  if (loading && !data) return <div className={s.empty}>Loading…</div>;
  const seg = data?.segment;
  if (!seg) return <div className={s.empty}>No attribution data for this window.</div>;
  const t = data.thresholds || {};

  if (!seg.terms) {
    return (
      <div className={s.empty}>
        No term you appear on reaches {t.word_count_at_least} words, so there is no tail to
        value at that threshold.
      </div>
    );
  }

  return (
    <>
      <div className={s.figures} style={{ marginBottom: 18 }}>
        <div className={s.figure}>
          <span className={s.figureLabel}>Tail revenue</span>
          <span className={s.figureValue}>{money(seg.revenue)}</span>
          <span className={s.basis}>allocated by us</span>
        </div>
        <div className={s.figure}>
          <span className={s.figureLabel}>Of allocated revenue</span>
          <span className={s.figureValue}>{pct(seg.share_of_allocated_revenue, 0)}</span>
          <span className={s.figureNote}>
            out of {num(seg.priced_terms_total)} terms that carried a purchase at all
          </span>
        </div>
        <div className={s.figure}>
          <span className={s.figureLabel}>Of search clicks</span>
          <span className={s.figureValue}>{pct(seg.share_of_search_clicks, 0)}</span>
          <span className={s.figureNote}>{num(seg.clicks)} clicks across the tail</span>
        </div>
        <div className={s.figure}>
          <span className={s.figureLabel}>Margin</span>
          <span className={s.figureValue}>—</span>
          <span className={s.figureNote}>{seg.margin_note}</span>
        </div>
      </div>

      <p className={s.hint} style={{ marginBottom: 18 }}>
        {num(seg.terms)} terms of {t.word_count_at_least} words or more across{' '}
        {num(seg.products)} products. {num(seg.terms_valued)} carried a purchase and could be
        valued; the other {num(seg.terms_without_purchases)} brought clicks and no purchase
        this window, so they are worth nothing — an answer, not a valuation that failed.
        {seg.purchases_unvalued > 0 && (
          <> {num(seg.purchases_unvalued)} tail purchases could not be placed because those
            terms have no median price.</>
        )}
      </p>

      {data.by_product?.length > 0 && (
        <section style={{ marginBottom: 22 }}>
          <h4 className={s.expansionTitle}>Tail by product</h4>
          <table className={s.subTable}>
            <thead>
              <tr><th>ASIN</th><th className={s.right}>Tail terms</th>
                <th className={s.right}>Valued</th><th className={s.right}>Revenue</th>
                <th className={s.right}>Of its search clicks</th></tr>
            </thead>
            <tbody>
              {data.by_product.slice(0, 25).map((p) => (
                <tr key={p.asin}>
                  <td>{p.asin}</td>
                  <td className={s.right}>{num(p.terms)}</td>
                  <td className={s.right}>{num(p.terms_valued)}</td>
                  <td className={s.right}>{money(p.revenue)}</td>
                  <td className={s.right}>{pct(p.share_of_search_clicks, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section>
        <h4 className={s.expansionTitle}>Long-tail terms you are not on</h4>
        {data.candidates?.length ? (
          <>
            <p className={s.hint} style={{ marginBottom: 10 }}>
              {num(t.long_tail_candidates)} of the {num(t.scanned)} terms you get no impression
              on reach {t.word_count_at_least} words
              {t.concentration_at_most != null && (
                <> , and {num(data.candidates.length)} of those have the top three ASINs
                  holding at most {pct(t.concentration_at_most, 0)} of the clicks</>
              )}. {t.basis}
              {t.concentration_unmeasured > 0 && (
                <> {num(t.concentration_unmeasured)} could not be measured against that bar and
                  are left out rather than counted as open.</>
              )}
            </p>
            <table className={s.subTable}>
              <thead>
                <tr><th>Term</th><th className={s.right}>Words</th>
                  <th className={s.right}>Search rank</th><th className={s.right}>Top-3 hold</th>
                  <th>Who wins it</th></tr>
              </thead>
              <tbody>
                {data.candidates.slice(0, 50).map((c) => (
                  <tr key={c.search_term}>
                    <td>{c.search_term}</td>
                    <td className={s.right}>{c.words}</td>
                    <td className={s.right}>{c.rank == null ? '—' : `#${num(c.rank)}`}</td>
                    <td className={s.right}>{pct(c.concentration, 0)}</td>
                    <td>
                      <ul className={s.holdersInline}>
                        {(c.holders || []).slice(0, 3).map((h) => (
                          <li key={h.asin}>
                            <span className={h.competitor ? s.holderRival : s.holderOther}>
                              {h.competitor || h.asin}
                            </span>
                            <span className={s.muted}> {pct(h.click_share, 0)}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p className={s.hint}>
            No long-tail term you are absent from clears the concentration bar for this window
            {t.scanned ? `, out of ${num(t.scanned)} scanned` : ''}. Terms you are absent from
            need the marketplace Top Search Terms report.
          </p>
        )}
      </section>
    </>
  );
}
